// Live's "Add layer" for a picture (spec G13b, PR D1): a chosen picture becomes a new top layer,
// centred and shrunk only when larger than the document, its size from what the API records, else
// from the loaded picture (live's `layer-content.ts`).
import { ApiError } from "../../../api/errors";
import { getAsset } from "../../../api/generated/endpoints/assets/assets";
import { getJob } from "../../../api/generated/endpoints/generation/generation";
import type { SourceRef } from "../../../api/generated/model";
import type { PickerLibrary } from "../../../platform/media/ImagePicker";
import type { ReadPicture } from "./draft";
import { LayerError, wordsOf } from "./errors";
import { containedFrame, type PictureLayer, type Size } from "./model";

/** A picture chosen in one of the three pickers, as a layer needs it. */
export interface PictureChoice {
  ref: SourceRef;
  source: "generation" | "library" | "product";
  /** The full picture, which the layer draws and the saved image is made of. */
  url: string;
  name: string;
  /** Its size where the API records it. */
  width?: number | null | undefined;
  height?: number | null | undefined;
}

/** Live's words when a chosen picture cannot become a layer. */
export const NOT_LOADED = "The selected image could not be loaded.";
export const NO_SIZE = "The selected image has no usable dimensions.";
export const NOT_ADDED = "The layer could not be added.";

function usable(size: { width?: number | null | undefined; height?: number | null | undefined }): size is Size {
  const { width, height } = size;
  return (
    typeof width === "number" && typeof height === "number" &&
    Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
  );
}

function loadSize(url: string): Promise<Size> {
  return new Promise((resolve, reject) => {
    const picture = new Image();
    picture.addEventListener("load", () => {
      const size = { width: picture.naturalWidth, height: picture.naturalHeight };
      if (usable(size)) resolve(size);
      else reject(new LayerError(NO_SIZE));
    });
    picture.addEventListener("error", () => reject(new LayerError(NOT_LOADED)));
    picture.src = url;
  });
}

/** How a picture's size is read when the API records none; a test's page loads no picture. */
export const PICTURE_SIZE = { load: loadSize };

/** The choice's size: the API's, else the loaded picture's. */
export function sizeOf(choice: PictureChoice): Promise<Size> {
  const recorded = { width: choice.width, height: choice.height };
  return usable(recorded) ? Promise.resolve(recorded) : PICTURE_SIZE.load(choice.url);
}

/** The new layer for a choice of that size, named as the picker names it. */
export function pictureLayer(id: string, choice: PictureChoice, size: Size, document: Size): PictureLayer {
  return {
    kind: "picture",
    id,
    name: choice.name,
    frame: containedFrame(document, size),
    source: choice.source,
    ref: choice.ref,
    url: choice.url,
    width: size.width,
    height: size.height,
  };
}

/** A failure's words: live's own where it said them, else "The layer could not be added." */
export function failureWords(error: unknown): string {
  return wordsOf(error, NOT_ADDED);
}

/**
 * How a kept draft's added picture is read again (`refreshAdded`): the full picture each picker
 * gave, as the API now signs it. A library or product photo is its file's original; an output of
 * My Generations or an AI edit is that output's URL, while it is delivered. The API's 404 means
 * the picture is gone; any other failure is thrown, for the editor's "Retry".
 */
export function pictureReader(library: PickerLibrary): ReadPicture {
  return async (ref: SourceRef) => {
    try {
      if (ref.kind === "asset") {
        const asset = await getAsset(library.teamId, library.brandId, ref.asset_id);
        return asset.urls.original ?? null;
      }
      const job = await getJob(library.teamId, ref.job_id);
      const output = job.outputs.find((each) => each.index === ref.output_index);
      return output?.status === "delivered" ? (output.url ?? null) : null;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  };
}
